/**
 * NextActionsChecklist - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows a dynamic checklist of next actions based on claim state.
 * Tracks completion and shows impact of each action.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  ClipboardList,
  CheckCircle,
  Circle,
  ArrowRight,
  AlertTriangle,
  Loader2,
  Sparkles
} from 'lucide-react';

interface NextAction {
  id: string;
  action: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  impact: string;
  completed: boolean;
  due_date?: string;
  route?: string;
}

interface NextActionsChecklistProps {
  reportId: string;
  className?: string;
  onActionComplete?: (actionId: string) => void;
  /** Pre-fetched slice from /dashboard/case-details. */
  data?: { actions?: NextAction[]; error?: string } | null;
  isLoading?: boolean;
}

const PRIORITY_STYLES: Record<NextAction['priority'], {
  bg: React.CSSProperties;
  border: React.CSSProperties;
  badge: React.CSSProperties;
  icon: React.CSSProperties;
}> = {
  critical: {
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    border: { borderColor: 'var(--color-needs-human-tint-bd)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    icon: { color: 'var(--color-needs-human-text)' }
  },
  high: {
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    border: { borderColor: 'var(--color-needs-human-tint-bd)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    icon: { color: 'var(--color-brand)' }
  },
  medium: {
    bg: { backgroundColor: 'var(--color-needs-human-tint)' },
    border: { borderColor: 'var(--color-needs-human-tint-bd)' },
    badge: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    icon: { color: 'var(--color-brand)' }
  },
  low: {
    bg: { backgroundColor: 'var(--color-bg-elevated)' },
    border: { borderColor: 'var(--color-border)' },
    badge: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' },
    icon: { color: 'var(--color-text-secondary)' }
  }
};

export default function NextActionsChecklist({
  reportId,
  className = '',
  onActionComplete,
  data: sliceData,
  isLoading: externalLoading,
}: NextActionsChecklistProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [actions, setActions] = useState<NextAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [completingId, setCompletingId] = useState<string | null>(null);

  const batchedMode = sliceData !== undefined;

  // Keep a local copy of the actions list so we can mark items complete
  // optimistically. In batched mode we seed this from the slice whenever
  // it arrives; subsequent "complete" clicks mutate the local state.
  useEffect(() => {
    if (batchedMode) {
      setActions(sliceData?.actions || []);
    }
  }, [batchedMode, sliceData]);

  useEffect(() => {
    if (batchedMode) return; // parent owns the fetch
    if (reportId) fetchActions();
  }, [reportId, batchedMode]);

  const effectiveLoading = batchedMode ? !!externalLoading : loading;
  const effectiveError = batchedMode
    ? !!(sliceData && 'error' in (sliceData || {}) && sliceData.error)
    : error;

  const fetchActions = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/next-actions/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setActions(data.actions || []);
    } catch (err) {
      console.debug('Next actions API not available');
      setActions([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleAction = async (action: NextAction) => {
    if (action.completed) return; // Can't uncomplete

    setCompletingId(action.id);
    try {
      const token = await getToken();
      await api.post(`/api/v1/dashboard/next-actions/${action.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });

      setActions(prev => prev.map(a =>
        a.id === action.id ? { ...a, completed: true } : a
      ));

      onActionComplete?.(action.id);
    } catch (err) {
      console.debug('Failed to mark action complete');
    } finally {
      setCompletingId(null);
    }
  };

  const completedCount = actions.filter(a => a.completed).length;
  const progress = actions.length > 0 ? (completedCount / actions.length) * 100 : 0;

  if (effectiveLoading) {
    return (
      <div className={`rounded-xl shadow-sm border p-6 ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-sm border ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <ClipboardList className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Next Actions</h3>
          </div>
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {completedCount}/{actions.length} complete
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, backgroundColor: 'var(--color-brand)' }}
          />
        </div>
      </div>

      {/* Actions list */}
      <div className="divide-y" style={{ borderColor: 'var(--color-border-faint)' }}>
        {effectiveError && actions.length === 0 ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Next actions unavailable</p>
            {!batchedMode && (
              <button
                onClick={fetchActions}
                className="mt-2 text-xs"
                style={{ color: 'var(--color-needs-human-text)' }}
              >
                Retry
              </button>
            )}
          </div>
        ) : actions.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-verified)' }} />
            <p className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>All actions complete!</p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>This claim is ready for the next step</p>
          </div>
        ) : (
          actions.map((action) => {
            const styles = PRIORITY_STYLES[action.priority];
            const isCompleting = completingId === action.id;

            return (
              <div
                key={action.id}
                className={`p-4 transition-all ${action.completed ? 'opacity-75' : ''}`}
                style={action.completed ? { backgroundColor: 'var(--color-bg-elevated)' } : styles.bg}
              >
                <div className="flex items-start">
                  <button
                    onClick={() => toggleAction(action)}
                    disabled={action.completed || isCompleting}
                    className="mt-0.5 mr-3 flex-shrink-0"
                  >
                    {isCompleting ? (
                      <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
                    ) : action.completed ? (
                      <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                    ) : (
                      <Circle className="w-5 h-5 transition-colors" style={styles.icon} />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h4 className={`font-medium ${action.completed ? 'line-through' : ''}`} style={{ color: action.completed ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}>
                        {action.action}
                      </h4>
                      {!action.completed && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium" style={styles.badge}>
                          {action.priority}
                        </span>
                      )}
                    </div>
                    <p className="text-sm mt-1" style={{ color: action.completed ? 'var(--color-text-dim)' : 'var(--color-text-secondary)' }}>
                      {action.description}
                    </p>
                    {!action.completed && (
                      <div className="flex items-center mt-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        <Sparkles className="w-3 h-3 mr-1" />
                        Impact: {action.impact}
                      </div>
                    )}
                  </div>

                  {action.route && !action.completed && (
                    <button
                      onClick={() => navigate(action.route!)}
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: 'var(--color-needs-human-text)' }}
                      title="Go to action"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
